import { createContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { jwtVerify } from "jose";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import {
  LoginGoogle,
  getClient,
  getMicrosoftUser,
  setUserJS,
} from "./Auth.api";
// import Loader from "../components/ui/Loader";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  //#region VARS --------------------------------------------------------
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [msalInstance, setMsalInstance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fullClient, setFullClient] = useState(null);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [shouldShowLogin, setShouldShowLogin] = useState(false);

  const manager = {
    clientId: process.env.REACT_APP_CLIENT_ID /* || "cbb" */,
    client: process.env.REACT_APP_CLIENT_LOGIN_ID /*||
       "Workobot", //  "Bolivar Bogota"*/,
    domainName: process.env.REACT_APP_LOGIN_DOMAIN_NAME /*||
       "workobot", //  "constructorabolivar"*/,
    enableAuthSystem: process.env.REACT_APP_ENABLE_AUTH_SYSTEM /* || "true" */,
    authMethodsOverride:
      process.env.REACT_APP_AUTH_METHODS_OVERRIDE?.split(",") /* || [
      "workobot",
      "microsoft",
      "google",
    ] */,
  };
  const monday = "8dF3$2sdfjH3^92!kLqP4tMvNxY@6#e1"; //SECRET_KEY
  //#endregion VARS -----------------------------------------------------

  useEffect(() => {
    setUserJS(user); // Se envía en un setter para que lo puedan usar los .js
  }, [user]);

  async function fetchClient() {
    setIsLoading(true);
    try {
      const clientData = await getClient(manager.client, manager.domainName);
      if (clientData) {
        setFullClient(clientData);
      } else {
        console.error("Error: no se devolvieron datos del cliente");
      }
    } catch (error) {
      console.error("Error al obtener el cliente", error);
    } finally {
      setIsLoading(false);
    }
  }

  // Validar token
  const validateToken = async (token, lsKey) => {
    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decodedToken.exp < currentTime) {
        console.log("Token expirado.", decodedToken.exp, currentTime);

        localStorage.removeItem(lsKey);
        setShouldShowLogin(true);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error decodificando el token:", error);
      return false;
    }
  };

  const validateAndLoginGoogle = async (g_wkt) => {
    const [jwt, hubCode] = JSON.parse(g_wkt);
    if ((await validateToken(jwt, "g_wkt")) && !isAuthenticated) {
      try {
        const result = await LoginGoogle(jwt, hubCode);
        if (result.Message !== "VALID USER") {
          throw new Error("Invalid user");
        }
        setUser(result.user);
        setIsAuthenticated(true);
        return;
      } catch (error) {
        console.error("Error al iniciar sesión:", error);
        localStorage.removeItem("g_wkt");
      }
    }
  };

  const validateAndLoginMicrosoft = async (ms_wkt) => {
    const [accessToken, hubCode] = JSON.parse(ms_wkt);
    if ((await validateToken(accessToken, "ms_wkt")) && !isAuthenticated) {
      try {
        const result = await getMicrosoftUser(accessToken, hubCode);
        if (result.id) {
          let resUser = {
            user: result.userPrincipalName,
            userNames: result.displayName,
            id: result.id,
          };
          setUser(resUser);
          setIsAuthenticated(true);
        } else {
          throw new Error("Invalid user");
        }
      } catch (error) {
        console.error("Error al iniciar sesión con Microsoft:", error);
        localStorage.removeItem("ms_wkt");
      }
    }
  };

  useEffect(() => {
    // Si enableAuthSystem no está habilitado, marcar la autenticación como inicializada y salir
    if (manager.enableAuthSystem === "false") {
      setIsAuthInitialized(true);
      let resUser = {
        user: "",
        userNames: "",
      };
      setUser(resUser);
      setIsAuthenticated(true);
      return;
    }

    const initializeAuth = async () => {
      if (fullClient?.authMethods) {
        const { google, microsoft, workobot } = fullClient.authMethods;

        //Workobot -------------------
        if (workobot) {
          const token = localStorage.getItem("w_wkt");
          if (token && !isAuthenticated) {
            try {
              const secret = new TextEncoder().encode(monday);
              const { payload } = await jwtVerify(token, secret);

              // Verificar si el token ha expirado
              setUser(payload.user);
              setIsAuthenticated(true);
            } catch (error) {
              console.error(
                "Error verificando el token de autenticación:",
                error
              );
              setShouldShowLogin(true); // Si expira se muestra el login
              localStorage.removeItem("w_wkt");
            }
          }
        }

        //Google ---------------------
        if (google || manager.authMethodsOverride?.includes("google")) {
          const g_wkt = localStorage.getItem("g_wkt");
          if (g_wkt) {
            await validateAndLoginGoogle(g_wkt);
          }
        }

        //Microsoft -------------------
        if (microsoft) {
          const ms_wkt = localStorage.getItem("ms_wkt");
          if (ms_wkt) {
            await validateAndLoginMicrosoft(ms_wkt);
          }
        }
      }
      setIsAuthInitialized(true); // Marcar como inicializado después de intentar autenticación
    };

    if (fullClient) {
      initializeAuth();
    }
  }, [fullClient]);

  useEffect(() => {
    // Si enableAuthSystem no está habilitado, no inicializar Microsoft Authentication Library
    if (manager.enableAuthSystem === "false") {
      return;
    }

    if (fullClient?.authMethods?.microsoft && !msalInstance) {
      const authority =
        fullClient.authMethods.microsoft.authority ||
        process.env.REACT_APP_MICROSOFT_AUTHORITY;
      const clientId =
        fullClient.authMethods.microsoft.clientId ||
        process.env.REACT_APP_MICROSOFT_CLIENT_ID;

      const config = {
        auth: {
          clientId,
          authority: `https://login.microsoftonline.com/${authority}`,
          redirectUri: window.location.origin,
        },
        cache: {
          cacheLocation: "sessionStorage",
          storeAuthStateInCookie: false,
        },
      };

      const instance = new PublicClientApplication(config);
      setMsalInstance(instance);
    }
  }, [fullClient]);

  useEffect(() => {
    if (manager.enableAuthSystem === "true") fetchClient();
  }, []);

  // if (isLoading) {
  //   return <Loader myText="cargando..." />;
  // }

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        manager,
        user,
        monday,
        fullClient,
        shouldShowLogin,
        isAuthenticated,
        isAuthInitialized,
        setUser,
        setMsalInstance,
        setIsAuthenticated,
        setShouldShowLogin,
        setIsAuthInitialized,
      }}
    >
      {msalInstance ? (
        <MsalProvider instance={msalInstance}>{children}</MsalProvider>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
